import { defineComponent as _defineComponent } from 'vue'
import ascendingOrder from '@/assets/ascending-order.png'
import descendingOrder from '@/assets/descending-order.png'
import disorder from '@/assets/disorder.png'
import { translate } from '@/util/commonFun'
import { IFilterableFieldsType } from '@/views/materialsList/types/material'
import { PlankType } from '@/views/materialsList/types/part'
import {
  computed,
  nextTick,
  onMounted,
  reactive,
  ref,
  watch,
  withDefaults,
} from 'vue'
import { List as virtualList } from 'vxe-table'

import { IPropList } from './config/tableConfig'
import singleVirtualList from './singleVirtualList.vue'
import virtualHead from './virtualListHead.vue'

// 排序类型定义
type SortType = 0 | 1 | 2 // 0 无序 1 升序 2 降序
type SortDirection = 'none' | 'asc' | 'desc'
type SortableField = 'specHeight' | 'specWidth'

interface SortEvent {
  field: string
  sortType: SortType
  sortDirection: SortDirection
}

// eslint-disable-next-line no-unused-vars
type Func = (plank: PlankType) => number
interface IProps {
  // 料单所需字段处理函数
  filterAmount?: Func
  // 料单所需字段处理函数
  filterArea?: Func
  // 表格数据
  tableData: any
  // 判断表格数据是否为空
  isempty?: boolean
  // 表格配置数据
  tableHeadProp: any
  // 是否显示内容体
  showTableBody?: boolean
  // 是否显示单个板件以及整体边框(不包含单个材质表头)
  border?: boolean
  // 是否可以拖动表头宽度
  isDragTableHead?: boolean
  // 单条高度
  singleItemHeight?: number
  // 最多展示多少条数据
  showDataLength?: number
  // 头部选择框区域大小
  selectBoxWidth?: number
  // 对齐方式
  align?: 'left' | 'center'
  // 是否显示表头边框
  showHeadBorder?: boolean
  // 每一条边框的颜色
  borderColor?: string
  // 是否显示回到顶部
  isShowBackTop?: boolean
  // 不需要省略号
  noEllipsis?: string[]
  filterKeys?: string[]
  // 是否需要过滤
  hasFilter?: boolean
  // id的前缀
  preffixID?: string
  // 超长板件的uniqueID
  extraLongPlatesUniqueID?: string
}
// TODO: 排序字段需要记住
// 切换页面需要记住被排序的字段极其排序类型
// 保存历史也需要记住当前历史被排序的字段极其排序类型

export default /*#__PURE__*/_defineComponent({
  __name: 'materialVirtualList',
  props: {
    filterAmount: { type: Function, default: () => ({} as any) },
    filterArea: { type: Function, default: () => ({} as any) },
    tableData: null,
    isempty: { type: Boolean, default: true },
    tableHeadProp: null,
    showTableBody: { type: Boolean, default: true },
    border: { type: Boolean, default: false },
    isDragTableHead: { type: Boolean, default: true },
    singleItemHeight: { default: 49 },
    showDataLength: { default: 16 },
    selectBoxWidth: { default: 64 },
    align: { default: 'center' },
    showHeadBorder: { type: Boolean, default: true },
    borderColor: { default: '#ccc' },
    isShowBackTop: { type: Boolean, default: true },
    noEllipsis: { default: () => [] },
    filterKeys: null,
    hasFilter: { type: Boolean },
    preffixID: null,
    extraLongPlatesUniqueID: { default: '' }
  },
  emits: ["inputBlur", "cellClick", "changeSelect", "changeSelectAll", "onScrollBottom", "headRowChange", "inputChange", "onPlankFilter", "toggleFold", "onSort"],
  setup(__props: any, { expose, emit: emits }: { emit: ({
  (e: 'inputBlur', params: any): void
  (e: 'cellClick', ...args: any[]): void
  (e: 'changeSelect', ...args: any[]): void
  (e: 'changeSelectAll', params: any): void
  (e: 'onScrollBottom'): void
  (e: 'headRowChange', params: any): void
  (e: 'inputChange', params: any): void
  (e: 'onPlankFilter', params: { prop: IFilterableFieldsType }): void
  (e: 'toggleFold', plank: PlankType): void
  (e: 'onSort', params: SortEvent): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as { filterAmount: Func, filterArea: Func, tableData: any, isempty: boolean, tableHeadProp: any, showTableBody: boolean, border: boolean, isDragTableHead: boolean, singleItemHeight: number, showDataLength: number, selectBoxWidth: number, align: 'left' | 'center', showHeadBorder: boolean, borderColor: string, isShowBackTop: boolean, noEllipsis: string[], filterKeys?: string[], hasFilter?: boolean, preffixID?: string, extraLongPlatesUniqueID: string }

// import { getBrowserScrollbarWidth } from '@/util/plankCommonFuncs'
const sortType = ref<SortType>(0) // 0 无序 1 升序 2 降序
const currentSortField = ref<string>('') // 当前排序字段
const sortTypeToIcon = {
  0: disorder,
  1: ascendingOrder,
  2: descendingOrder,
} as const

const orderIcon = computed(() => {
  return sortTypeToIcon[sortType.value] || disorder
})

// 排序字段配置
const sortableFields: readonly SortableField[] = [
  'specHeight',
  'specWidth',
] as const




// 状态
const state = reactive({
  //唯一ID
  uniqueID: '',
  // 字段
  field: '',
  // 验证
  isVerifyPass: true,
})

// 重置线div元素
const resizableLine = ref<HTMLDivElement | null>(null)
const currentTarget = ref<HTMLDivElement>()
const currentLocation = ref(0)
const currentField = ref('')
const containerRef = ref<HTMLDivElement | null>()
// 是否显示重置线
const isShowResizableLine = ref(false)
// 容器在窗口的位置
const offsetX = ref(0)
// 记录最后一个元素的原始宽度
const recodeLastElWidth = ref('')
const browserScrollWidth = ref(17)
const browserScrollShow = ref(false)
const singleItemNeedProp = ref()
// 控制显示高度
const tableHeight = computed(() => {
  const { tableData, showDataLength, singleItemHeight } = props
  const len = tableData.length ?? 0
  return len >= showDataLength
    ? showDataLength * singleItemHeight
    : len * singleItemHeight
})
// 表格配置信息
const tableHeadProp = ref([...props.tableHeadProp])

const listRef = ref()
// 挂载
onMounted(() => {
  recodeLastElWidth.value = tableHeadProp.value.slice(-1)[0].width
  // 获取浏览器当前滚动条宽度
  // browserScrollWidth.value = getBrowserScrollbarWidth()
  browserScrollWidth.value = 10
  // changeTablePropLastWidth()
  changeLastWidth()
})
const containerFinalWidth = computed(() => {
  const width = tableHeadProp.value.reduce(
    (pre, cut) => pre + +cut.width.replace('px', ''),
    0
  )
  return width
})

const tableInputComponents = computed(() => {
  return tableHeadProp.value.filter(
    (item) => item.interactionType && item.interactionType == 'input'
  )
})
const translateLang = (key: string) => {
  return translate(key)
}
// 监听props改变
watch(
  () => tableHeadProp.value,
  () => {
    changeLastWidth()
  },
  { deep: true }
)
watch(
  () => props.tableHeadProp,
  () => {
    tableHeadProp.value = [...props.tableHeadProp]
    changeLastWidth()
  },
  { deep: true }
)
watch(
  () => props.tableData,
  () => {
    changeLastWidth()
  }
)
watch(
  () => containerFinalWidth.value,
  () => {
    emits('headRowChange', containerFinalWidth.value)
  },
  { immediate: true }
)
watch(
  () => props.extraLongPlatesUniqueID,
  (newValue) => {
    window.scrollTo(0, document.documentElement.scrollHeight)
    if (newValue) {
      nextTick(() => {
        setTimeout(() => {
          // 调用滚动方法
          let index = listRef.value?.fullData.findIndex((item: any) => {
            return item.partUniqueId === newValue
          })
          listRef.value.scrollTo(0, (index - 1) * 49)
          state.uniqueID = newValue
        }, 0)
      })
    }
  },
  {
    immediate: true,
  }
)
// 点击筛选图标
function handlePlankFilter(item: IPropList) {
  emits('onPlankFilter', { prop: item.prop as IFilterableFieldsType })
}

// 处理每一行的配置信息
function changeLastWidth() {
  // 第一次加载滚动条不会出现，需要比对数量
  const copyData = JSON.parse(JSON.stringify(tableHeadProp.value))
  singleItemNeedProp.value = copyData
}
// 单元格点击
function itemClick({ part, field }: any) {
  if (state.isVerifyPass) {
    state.uniqueID = part.partUniqueId || ''
    state.field = field ?? ''
  }
  emits('cellClick', part, field)
}
expose({ itemClick })
// 输入框失焦
function inputBlur({ part, field, verifyPass }: any) {
  // 验证不通过
  if (!verifyPass) {
    state.isVerifyPass = false
    return
  }
  state.isVerifyPass = true
  state.uniqueID = part.partUniqueId
  state.field = ''
  const { materialBasePartIndex, materialBaseTableIndex } = part
  const plank = props.tableData.find(
    (it: any) =>
      it.isVirtualHead && it.materialBaseTableIndex === materialBaseTableIndex
  )
  emits('inputBlur', {
    part,
    plank,
    key: field,
    plankIndex: materialBasePartIndex,
    tableIndex: materialBaseTableIndex,
  })
}
// 输入变化
function inputChange(data: any) {
  emits('inputChange', data)
}
// 单选
function changeSelect({ part }: any) {
  const { materialBaseTableIndex } = part

  const tableData = props.tableData.find(
    (it: any) =>
      it.isVirtualHead && it.materialBaseTableIndex === materialBaseTableIndex
  )
  emits('changeSelect', tableData, part)
}
// 多选
function changeSelectAll(plank: any) {
  emits('changeSelectAll', plank)
}
// 控制宽度
function mouseResizableDown(e: MouseEvent, headItem: any) {
  if (!props.isDragTableHead) return
  const { prop } = headItem
  isShowResizableLine.value = true
  // 保存当前点击字段
  currentField.value = prop
  // 保存点击的元素
  currentTarget.value = e.target as HTMLDivElement
  currentLocation.value = e.clientX
  // 获取整个容器在窗口的位置

  const rect = containerRef.value?.getBoundingClientRect()

  offsetX.value = rect?.left ?? 0
  changeResizableLocation()
}
// 鼠标移动
function containerMove(e: MouseEvent) {
  if (!isShowResizableLine.value) return
  const { clientX } = e

  const offset = clientX - currentLocation.value

  changeResizableLocation(offset)
}
// 鼠标抬起
function containerUp(e: MouseEvent) {
  if (!isShowResizableLine.value) return
  const target = tableHeadProp.value.find(
    (it) => it.prop === currentField.value
  )
  if (!target) return
  const { clientX } = e
  const offset = clientX - currentLocation.value
  const width = +target.width.replace('px', '') + offset
  // 最小宽度
  const minWidth = +target.minWidth.replace('px', '')
  // 最终宽度
  const finalWidth = width <= minWidth ? minWidth : width
  target.width = `${finalWidth}px`
  // nextTick(() => {
  //   changeTablePropLastWidth()
  // })
  isShowResizableLine.value = false
}
// 变更重置线位置
function changeResizableLocation(offset = 0) {
  const el = resizableLine.value
  const rect = currentTarget.value?.getBoundingClientRect()
  if (el && rect) {
    // 元素宽度
    const elWidth = (currentTarget.value?.offsetWidth ?? 0) / 2
    // 需要移动的距离
    const lineOffset = rect.left + elWidth - offsetX.value + offset
    el.style.left = lineOffset + 'px'
  }
}
// 给组件传入不同的props
function setProps(item: any, rowIndex: number) {
  const common = {
    border: props.border,
    selectBoxWidth: props.selectBoxWidth,
    browserScrollShow: browserScrollShow.value,
    browserScrollWidth: browserScrollWidth.value,
    tableHeadProp: singleItemNeedProp.value,
    align: props.align,
    borderColor: props.borderColor,
    singleItemHeight: props.singleItemHeight,
    rowIndex,
  }
  const compsProps = item.isVirtualHead
    ? {
        plank: item,
        filterArea: props.filterArea,
        filterAmount: props.filterAmount,
        showHeadBorder: props.showHeadBorder,
        ...common,
      }
    : {
        part: item,
        ...common,
        ...state,
      }
  return compsProps
}

// 不知道scroll事件的event类型
function handleScrollTable(e: any) {
  // if (e.scrollTop === tableHeight.value) {
  //   emits('onScrollBottom')
  // }
}

// 表头折叠、展开
const handleToggleFold = (plank: PlankType) => {
  emits('toggleFold', plank)
}
const virtualListRef = ref()
// 聚焦下一个输入框
function focusOnNextInput(options: any) {
  const { propName, uniqueID, rowIndex } = options
  // 查询当前的propName在tableInputComponents里面的位置，需要判断是不是当前行的最后一个输入框
  let index = tableInputComponents.value.findIndex((item) => {
    return item.prop === propName
  })
  let rIndex = virtualListRef.value.findIndex(
    (item: any) => item.part.partUniqueId == uniqueID
  )
  let nextRowIndex = 0
  let nextProp = {}
  try {
    if (index == tableInputComponents.value.length - 1) {
      // 判断当前的rowIndex是不是最后一行，如果是的话，结束方法
      if (rowIndex == props.tableData.length - 1) return
      // 选中下一行数据的第一个输入框
      nextRowIndex = rIndex + 1
      nextProp = tableInputComponents.value[0]
    } else {
      nextRowIndex = rIndex
      nextProp = tableInputComponents.value[index + 1]
    }
    if (virtualListRef.value[nextRowIndex].dealPartClick) {
      virtualListRef.value[nextRowIndex].dealPartClick(nextProp)
    } else {
      // 方法不存在时的处理
      console.warn('未查询到dealPartClick方法')
    }
  } catch (error) {
    console.error('聚焦下一个输入框时发生错误：', error)
  }
}

// 处理排序点击
function handleSortClick(field: string) {
  if (!sortableFields.includes(field as SortableField)) return

  if (currentSortField.value === field) {
    // 切换排序状态：0 -> 1 -> 2 -> 0
    sortType.value = ((sortType.value + 1) % 3) as SortType
  } else {
    // 新字段，重置为升序
    currentSortField.value = field
    sortType.value = 1
  }

  // 触发排序事件
  const sortDirection: SortDirection =
    sortType.value === 1 ? 'asc' : sortType.value === 2 ? 'desc' : 'none'

  emits('onSort', {
    field,
    sortType: sortType.value,
    sortDirection,
  })
}

// 获取排序图标样式
function getSortIconStyle(field: string) {
  if (!sortableFields.includes(field as SortableField)) return {}

  const isActive = currentSortField.value === field
  return {
    color: isActive ? '#18a8c7' : '#999',
    cursor: 'pointer',
    transition: 'color 0.2s ease',
  }
}

return { __sfc: true,sortType, currentSortField, sortTypeToIcon, orderIcon, sortableFields, emits, props, state, resizableLine, currentTarget, currentLocation, currentField, containerRef, isShowResizableLine, offsetX, recodeLastElWidth, browserScrollWidth, browserScrollShow, singleItemNeedProp, tableHeight, tableHeadProp, listRef, containerFinalWidth, tableInputComponents, translateLang, handlePlankFilter, changeLastWidth, itemClick, inputBlur, inputChange, changeSelect, changeSelectAll, mouseResizableDown, containerMove, containerUp, changeResizableLocation, setProps, handleScrollTable, handleToggleFold, virtualListRef, focusOnNextInput, handleSortClick, getSortIconStyle, disorder, virtualList, singleVirtualList, virtualHead }
}

})